import { useInfiniteQuery } from "@tanstack/react-query";

import { useCallback, useMemo } from "react";

import { Unit } from "@/types/enums/unit";
import type {
  CreateMedicationData,
  Medication,
  MergeMedicationsPreview,
  MergeMedicationsRequest,
  UpdateMedicationData,
} from "@/types/medication";
import { InventoryDrawdownTiming } from "@/types/medication";

import { requestData } from "@/api/api-config";

export interface DoseInventoryPreviewInput {
  patientId: string;
  providerId?: string | null;
  locationId?: string | null;
  doseComponentId?: string | null;
  compoundMedicationId?: string | null;
  amount?: number | null;
  unit?: Unit | null;
  quantity: number;
  dilutionId?: string | null;
}
export interface DoseInventoryPreview {
  stocks: Array<{
    stockId: string;
    unit: Unit | null;
    perAdministration: number;
    originalPerAdministration: number | null;
    target: number;
    originalTarget: number;
    outstanding: number;
  }>;
  ownedLots: Array<{
    shipmentId: string;
    stockId: string;
    outstanding: number;
    locationId?: string | null;
    allocationProviderId?: string | null;
  }>;
  allowsClinicalLotReferences?: boolean;
  ordinaryEditMovesInventory: boolean;
  drawsAtDose: boolean;
  drawdownTiming: InventoryDrawdownTiming;
}

export const doseInventoryPreviewOptions = (
  medicationId: string,
  input: DoseInventoryPreviewInput,
) => ({
  queryKey: ["medications", medicationId, "inventory-preview", input] as const,
  queryFn: async () => {
    const result = await requestData<DoseInventoryPreview>(
      `medications/${medicationId}/inventory-preview`,
      "post",
      { json: input },
      false,
    );
    if (!result) throw new Error("Inventory preview unavailable");
    return result;
  },
});

/** Lightweight shipment type returned by the available-lots endpoint. */
export interface ShipmentLot {
  id: string;
  stockId: string;
  locationId: string;
  supplierId?: string | null;
  supplierName?: string | null;
  lotNumber?: string;
  expiryDate?: string;
  ndc?: string;
  remainingQuantity: number;
  /** This receipt is recorded on the scoped dose, clinically or through actual inventory use. */
  isDoseComponentShipment?: boolean;
  /** Authoritative permission to offer new extra yield, distinct from historical reuse. */
  extraYieldEligible?: boolean;
  /** Actual outstanding physical draw for this exact receipt / its whole backing stock. */
  doseComponentOutstandingQuantity?: number | null;
  doseComponentStockOutstandingQuantity?: number | null;
  /** Backend-calculated saved component demand, never a substitute for ledger credit. */
  doseComponentOriginalTargetQuantity?: number | null;
  doseComponentOrdinaryEditMovesInventory?: boolean | null;
  quantity: number;
  cost?: number;
  createdDate?: string;
  // Provider-aware quantities are present only when this shipment has active
  // allocations. Available = this provider's own allocation + unassigned stock.
  allocatedRemainingQuantity?: number | null;
  unallocatedRemainingQuantity?: number | null;
  providerAvailableQuantity?: number | null;
  // Billed quantity on the next unclaimed checkout line for this patient and shipment today.
  chargedTodayQuantity?: number | null;
  /** Number of unclaimed checkout lines available to claim from this exact shipment. */
  chargedTodayChargeCount?: number | null;
  // A bare lot code is not verifiable at the point of care — two products can carry codes
  // one character apart — so every lot names its product.
  medicationId?: string | null;
  medicationName?: string | null;
  category?: string | null;
}

export interface BulkMedicationUpdateItem {
  id: string;
  name?: string;
  unit?: string | null;
  brandName?: string | null;
  price?: number;
  cost?: number | null;
  category?: string;
  sortOrder?: number | null;
  allowCustomPricing?: boolean;
  defaultCptCode?: string | null;
  defaultModifiers?: string[] | null;
  defaultIcd10Codes?: string[] | null;
  defaultPlaceOfService?: string | null;
}

export interface BulkMedicationUpdateRequest {
  updates: BulkMedicationUpdateItem[];
}

export interface MedicationIngredientOption {
  id: string;
  name: string;
  unit?: string | null;
  isArchived?: boolean;
  isCompound?: boolean;
}

interface MedicationIngredientOptionsPage {
  data: MedicationIngredientOption[];
  total: number;
}

// Fetch functions
async function fetchMedications(): Promise<Medication[]> {
  const res = await requestData<Medication[]>(
    "medications",
    "get",
    undefined,
    false, // Don't catch errors - let them bubble up
  );
  return res ?? [];
}

export async function fetchMedication(id: string): Promise<Medication | null> {
  return requestData<Medication>(
    `medications/${id}`,
    "get",
    undefined,
    false, // Don't catch errors - let them bubble up
  );
}

async function fetchShopEditorIngredientOptions({
  excludeId,
  search,
  page,
  perPage,
}: {
  excludeId: string;
  search: string;
  page: number;
  perPage: number;
}): Promise<MedicationIngredientOptionsPage> {
  const res = await requestData<MedicationIngredientOptionsPage>(
    "medications/shop-editor-ingredient-options",
    "get",
    { searchParams: { excludeId, search, page, perPage } },
    false,
  );
  return res ?? { data: [], total: 0 };
}

async function createMedication(
  data: CreateMedicationData,
): Promise<Medication> {
  const res = await requestData<Medication>(
    "medications",
    "post",
    { json: data },
    false, // Don't catch errors - let them bubble up
  );
  if (!res) throw new Error("Failed to create medication");
  return res;
}

async function updateMedication(
  id: string,
  data: UpdateMedicationData,
): Promise<Medication> {
  const res = await requestData<Medication>(
    `medications/${id}`,
    "put",
    { json: data },
    false, // Don't catch errors - let them bubble up
  );
  if (!res) throw new Error("Failed to update medication");
  return res;
}

async function deleteMedication(id: string): Promise<boolean> {
  const res = await requestData<boolean>(
    `medications/${id}`,
    "delete",
    undefined,
    false, // Don't catch errors - let them bubble up
  );
  return res ?? false;
}

async function bulkUpdateMedications(
  data: BulkMedicationUpdateRequest,
): Promise<Medication[]> {
  const res = await requestData<Medication[]>(
    "medications/bulk",
    "put",
    { json: data },
    false,
  );
  if (!res) throw new Error("Failed to bulk update medications");
  return res;
}

async function getAvailableLots(
  medicationId: string,
  locationId?: string,
  providerId?: string,
  doseComponentId?: string,
  includeDepleted?: boolean,
  patientId?: string,
  search?: string,
): Promise<ShipmentLot[] | null> {
  const searchParams: Record<string, string> = {};
  if (locationId) searchParams.location_id = locationId;
  if (providerId) searchParams.provider_id = providerId;
  if (doseComponentId) searchParams.dose_component_id = doseComponentId;
  if (patientId) searchParams.patient_id = patientId;
  if (includeDepleted) searchParams.include_depleted = "true";
  if (search?.trim()) searchParams.search = search.trim();
  return requestData<ShipmentLot[]>(
    `medications/${medicationId}/available-lots`,
    "get",
    Object.keys(searchParams).length ? { searchParams } : undefined,
  );
}

/**
 * Lot-numbered stock across the catalog, optionally narrowed to one category.
 *
 * The deliberate way out of the picker's product scope: used only when the medication
 * being documented cannot be resolved, or when its own lots came back empty.
 */
async function getAvailableLotsAcrossMedications(
  category?: string,
  locationId?: string,
  providerId?: string,
): Promise<ShipmentLot[] | null> {
  const searchParams: Record<string, string> = {};
  if (category) searchParams.category = category;
  if (locationId) searchParams.location_id = locationId;
  if (providerId) searchParams.provider_id = providerId;
  return requestData<ShipmentLot[]>(
    "medications/available-lots",
    "get",
    Object.keys(searchParams).length ? { searchParams } : undefined,
  );
}

/** Categories that have lot-numbered stock on hand — the widened picker's filter. */
async function getLotCategories(locationId?: string): Promise<string[] | null> {
  return requestData<string[]>(
    "medications/lot-categories",
    "get",
    locationId ? { searchParams: { location_id: locationId } } : undefined,
  );
}

async function previewMergeMedications(
  data: MergeMedicationsRequest,
): Promise<MergeMedicationsPreview> {
  const res = await requestData<MergeMedicationsPreview>(
    "medications/merge/preview",
    "post",
    { json: data },
    false,
  );
  if (!res) throw new Error("Failed to preview merge");
  return res;
}

async function mergeMedications(
  data: MergeMedicationsRequest,
): Promise<boolean> {
  const res = await requestData<boolean>(
    "medications/merge",
    "post",
    { json: data },
    false,
  );
  return res ?? false;
}

async function bulkArchiveMedications(ids: string[]): Promise<boolean> {
  const res = await requestData<boolean>(
    "medications/bulk",
    "delete",
    { json: { ids } },
    false,
  );
  return res ?? false;
}

// Query keys
export const medicationsQueryKeys = {
  all: ["medications"] as const,
  lists: () => [...medicationsQueryKeys.all, "list"] as const,
  list: () => [...medicationsQueryKeys.lists()] as const,
  details: () => [...medicationsQueryKeys.all, "detail"] as const,
  detail: (id: string) => [...medicationsQueryKeys.details(), id] as const,
  shopEditorIngredientOptions: (excludeId: string, search: string) =>
    [
      ...medicationsQueryKeys.all,
      "shop-editor-ingredient-options",
      excludeId,
      search,
    ] as const,
  availableLots: (
    medicationId: string,
    locationId?: string,
    providerId?: string,
    doseComponentId?: string,
    includeDepleted?: boolean,
    patientId?: string,
  ) =>
    [
      ...medicationsQueryKeys.all,
      "available-lots",
      medicationId,
      locationId,
      providerId,
      doseComponentId,
      includeDepleted ?? false,
      patientId,
    ] as const,
  availableLotsAcrossMedications: (
    category?: string,
    locationId?: string,
    providerId?: string,
  ) =>
    [
      ...medicationsQueryKeys.all,
      "available-lots-across-medications",
      category ?? null,
      locationId ?? null,
      providerId ?? null,
    ] as const,
  lotCategories: (locationId?: string) =>
    [
      ...medicationsQueryKeys.all,
      "lot-categories",
      locationId ?? null,
    ] as const,
};

// Query options
const medicationsQueryOptions = {
  list: () => ({
    queryKey: medicationsQueryKeys.list(),
    queryFn: () => fetchMedications(),
  }),
  detail: (id: string) => ({
    queryKey: medicationsQueryKeys.detail(id),
    queryFn: () => fetchMedication(id),
  }),
  shopEditorIngredientOptions: (
    excludeId: string,
    search = "",
    page = 1,
    perPage = 25,
  ) => ({
    queryKey: [
      ...medicationsQueryKeys.shopEditorIngredientOptions(excludeId, search),
      page,
    ] as const,
    queryFn: () =>
      fetchShopEditorIngredientOptions({ excludeId, search, page, perPage }),
  }),
  availableLots: (
    medicationId?: string,
    locationId?: string,
    providerId?: string,
    doseComponentId?: string,
    includeDepleted?: boolean,
    patientId?: string,
  ) => ({
    queryKey: medicationsQueryKeys.availableLots(
      medicationId ?? "",
      locationId,
      providerId,
      doseComponentId,
      includeDepleted,
      patientId,
    ),
    queryFn: () =>
      getAvailableLots(
        medicationId!,
        locationId,
        providerId,
        doseComponentId,
        includeDepleted,
        patientId,
      ),
    enabled: !!medicationId,
    refetchOnMount: "always" as const,
  }),
  availableLotsAcrossMedications: (
    category?: string,
    locationId?: string,
    providerId?: string,
  ) => ({
    queryKey: medicationsQueryKeys.availableLotsAcrossMedications(
      category,
      locationId,
      providerId,
    ),
    queryFn: () =>
      getAvailableLotsAcrossMedications(category, locationId, providerId),
  }),
  lotCategories: (locationId?: string) => ({
    queryKey: medicationsQueryKeys.lotCategories(locationId),
    queryFn: () => getLotCategories(locationId),
  }),
  mergePreview: (data: MergeMedicationsRequest) => ({
    queryKey: [
      ...medicationsQueryKeys.all,
      "merge-preview",
      data.keepId,
      [...data.sourceIds].sort().join(","),
    ] as const,
    queryFn: () => previewMergeMedications(data),
    enabled: !!data.keepId && data.sourceIds.length > 0,
  }),
};

// Mutation options
export function useShopEditorIngredientOptions({
  excludeId,
  search = "",
  perPage = 25,
  enabled = true,
}: {
  excludeId: string;
  search?: string;
  perPage?: number;
  enabled?: boolean;
}) {
  const query = useInfiniteQuery({
    queryKey: medicationsQueryKeys.shopEditorIngredientOptions(
      excludeId,
      search,
    ),
    queryFn: ({ pageParam }) =>
      fetchShopEditorIngredientOptions({
        excludeId,
        search,
        page: pageParam,
        perPage,
      }),
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce(
        (sum, page) => sum + page.data.length,
        0,
      );
      return loadedCount < lastPage.total ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: enabled && Boolean(excludeId),
  });

  const options = useMemo(
    () => query.data?.pages.flatMap((page) => page.data) ?? [],
    [query.data?.pages],
  );

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;
  const fetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  return {
    ...query,
    options,
    hasMore: Boolean(query.hasNextPage),
    loadMore: fetchMore,
  };
}

const medicationsMutationOptions = {
  create: () => ({
    mutationFn: (data: CreateMedicationData) => createMedication(data),
  }),
  update: () => ({
    mutationFn: ({ id, data }: { id: string; data: UpdateMedicationData }) =>
      updateMedication(id, data),
  }),
  delete: () => ({
    mutationFn: (id: string) => deleteMedication(id),
  }),
  bulkUpdate: () => ({
    mutationFn: (data: BulkMedicationUpdateRequest) =>
      bulkUpdateMedications(data),
  }),
  bulkArchive: () => ({
    mutationFn: (ids: string[]) => bulkArchiveMedications(ids),
  }),
  merge: () => ({
    mutationFn: (data: MergeMedicationsRequest) => mergeMedications(data),
  }),
};

export {
  getAvailableLots,
  getAvailableLotsAcrossMedications,
  getLotCategories,
  medicationsQueryOptions,
  medicationsMutationOptions,
};
